import React, { type Dispatch, type SetStateAction } from 'react';

import { ActionPalette } from 'components/palette/ActionPalette';
import { WorkflowCanvas, type WorkflowCanvasHandle } from 'components/canvas/WorkflowCanvas';
import { type RunnerState, type StepColorMap } from 'components/modals/RunWorkflowModal';
import { WorkflowCallbacksContext } from 'state/WorkflowCallbacksContext';
import { FormatContext, type FormatMode } from 'state/FormatContext';
import { ValidationContext } from 'state/ValidationContext';
import type { ActionContract } from 'model/actions';
import type { ValidationResult } from 'model/validation';
import type { ViewMode } from 'state/ViewModeContext';
import type {
  WorkflowEdge,
  WorkflowEdgeData,
  WorkflowGraph,
  WorkflowNode,
} from 'model/xyflowMapping';

import EditorContextMenuController, { type ContextMenuState } from './EditorContextMenuController';
import EditorSidePanel, { type SidebarTab } from './EditorSidePanel';
import { StyledEditorBodyLayout, StyledEditorCanvasArea } from './EditorBody.styled';

interface WorkflowCallbacks {
  onRenameStep: (oldId: string, newId: string) => void;
  onAddNodeAfter: (sourceNodeId: string, actionId: string) => void;
}

interface OwnProps {
  // Layout flags
  showActionPalette: boolean;
  isRunningJob: boolean;
  viewMode: ViewMode;
  edgeType: string;
  formatMode: FormatMode;

  // Canvas
  canvasRef: React.Ref<WorkflowCanvasHandle>;
  graph: WorkflowGraph;
  validation: ValidationResult;
  stepRunColors: StepColorMap | null;
  workflowCallbacks: WorkflowCallbacks;
  availableActions: ActionContract[];
  selectedNode: WorkflowNode | null;
  contextMenu: ContextMenuState | null;
  workflowId: string;
  sidebarTab: SidebarTab;
  runnerState: RunnerState;
  selectedRunnerFile: File | null;

  // Canvas event handlers
  onApplyCanvasChange: (next: WorkflowGraph | ((prev: WorkflowGraph) => WorkflowGraph)) => void;
  onNodeSelected: (id: string | null) => void;
  onSelectionChange: (ids: string[]) => void;
  onNodesDeleted: (ids: string[]) => void;
  onNodeContextMenu: (x: number, y: number, nodeId: string) => void;
  onEdgeContextMenu: (x: number, y: number, edgeId: string) => void;
  onPaneContextMenu: (x: number, y: number) => void;
  onEdgeDoubleClick: (edgeId: string) => void;
  onNodeDragStart: () => void;
  onNodeDragStop: () => void;
  onConnectInputBinding: (
    targetId: string,
    fieldName: string,
    sourceId: string,
    outputName: string | undefined,
  ) => void;

  // Context-menu actions
  onCloseContextMenu: () => void;
  onDuplicateNode: (nodeId: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  onAddNodeAtPosition: (actionId: string, x: number, y: number, parentStepId?: string) => void;

  // Side panel actions
  allNodes: WorkflowNode[];
  allEdges: WorkflowEdge[];
  onSidebarTabChange: (tab: SidebarTab) => void;
  onNodeChange: (updated: WorkflowNode) => void;
  onRenameStep: (oldId: string, newId: string) => void;
  onEdgeDataChange: (edgeId: string, partial: Partial<WorkflowEdgeData>) => void;
  onAddEdge: (sourceId: string, targetId: string) => void;
  onRunnerStateChange: Dispatch<SetStateAction<RunnerState>>;
  onSelectedRunnerFileChange: (file: File | null) => void;
  onStepColorsChange: (colors: StepColorMap | null) => void;
  onIsRunningChange: (running: boolean) => void;
}

/**
 * The three-column editor body: action palette (optional), canvas, side
 * panel. Wraps the canvas region in the page-level context providers
 * (`FormatContext`, `ValidationContext`, `WorkflowCallbacksContext`).
 *
 * Owns no state — pure presentational wrapper around the canvas + side
 * panel composition. Memoised so re-renders of the parent's status bar
 * or modal don't churn the canvas.
 */
const EditorBody = (props: OwnProps) => {
  const {
    showActionPalette,
    isRunningJob,
    viewMode,
    edgeType,
    formatMode,
    canvasRef,
    graph,
    validation,
    stepRunColors,
    workflowCallbacks,
    availableActions,
    selectedNode,
    contextMenu,
    workflowId,
    sidebarTab,
    runnerState,
    selectedRunnerFile,
    onApplyCanvasChange,
    onNodeSelected,
    onSelectionChange,
    onNodesDeleted,
    onNodeContextMenu,
    onEdgeContextMenu,
    onPaneContextMenu,
    onEdgeDoubleClick,
    onNodeDragStart,
    onNodeDragStop,
    onConnectInputBinding,
    onCloseContextMenu,
    onDuplicateNode,
    onDeleteNode,
    onDeleteEdge,
    onAddNodeAtPosition,
    allNodes,
    allEdges,
    onSidebarTabChange,
    onNodeChange,
    onRenameStep,
    onEdgeDataChange,
    onAddEdge,
    onRunnerStateChange,
    onSelectedRunnerFileChange,
    onStepColorsChange,
    onIsRunningChange,
  } = props;

  return (
    <FormatContext.Provider value={formatMode}>
      <ValidationContext.Provider value={validation}>
        <WorkflowCallbacksContext.Provider value={workflowCallbacks}>
          <StyledEditorBodyLayout>
            {showActionPalette && (
              <aside className='action-palette-rail'>
                <ActionPalette />
              </aside>
            )}
            <StyledEditorCanvasArea className={isRunningJob ? 'canvas-running' : undefined}>
              <WorkflowCanvas
                ref={canvasRef}
                graph={graph}
                onGraphChange={onApplyCanvasChange}
                onNodeSelected={onNodeSelected}
                onSelectionChange={onSelectionChange}
                onNodesDeleted={onNodesDeleted}
                onNodeContextMenu={onNodeContextMenu}
                onEdgeContextMenu={onEdgeContextMenu}
                onEdgeDoubleClick={onEdgeDoubleClick}
                onPaneContextMenu={onPaneContextMenu}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                viewMode={viewMode}
                edgeType={edgeType}
                stepRunColors={stepRunColors}
                badEdgeIds={validation.badEdgeIds}
                onConnectInputBinding={onConnectInputBinding}
              />
              <EditorContextMenuController
                contextMenu={contextMenu}
                graph={graph}
                availableActions={availableActions}
                onClose={onCloseContextMenu}
                onDuplicateNode={onDuplicateNode}
                onDeleteNode={onDeleteNode}
                onDeleteEdge={onDeleteEdge}
                onAddNodeAtPosition={onAddNodeAtPosition}
              />
            </StyledEditorCanvasArea>
            <EditorSidePanel
              workflowId={workflowId}
              sidebarTab={sidebarTab}
              selectedNode={selectedNode}
              allNodes={allNodes}
              allEdges={allEdges}
              runnerState={runnerState}
              selectedRunnerFile={selectedRunnerFile}
              onSidebarTabChange={onSidebarTabChange}
              onNodeChange={onNodeChange}
              onRenameStep={onRenameStep}
              onEdgeDataChange={onEdgeDataChange}
              onDeleteEdge={onDeleteEdge}
              onAddEdge={onAddEdge}
              onRunnerStateChange={onRunnerStateChange}
              onSelectedRunnerFileChange={onSelectedRunnerFileChange}
              onStepColorsChange={onStepColorsChange}
              onIsRunningChange={onIsRunningChange}
            />
          </StyledEditorBodyLayout>
        </WorkflowCallbacksContext.Provider>
      </ValidationContext.Provider>
    </FormatContext.Provider>
  );
};

export default React.memo(EditorBody);
